/**
 * Image-Preprocessing-Lab — Glass-Box-Komponente für Phase 1 Step 3.
 *
 * Style 1:1 wie PerCellModelTest aus Phase2Ocr.tsx: Toggle-Button → embedded
 * Inline-Panel. Drei Bild-Varianten zum Testen + Live-Anchor-Quality-Measure.
 *
 * Audit-Trail:
 *   - Original-PDF bleibt unverändert (links als iframe-Preview)
 *   - Jede getestete Variante: eigener PNG-File + Measure-JSON + UUID
 *   - DB hält active_variant + Variant-Liste
 *   - "Zurück zu Original" jederzeit möglich
 *
 * Auto-Activate (Glass-Box-Quality-Gate): Wenn baseline match_ratio < 0.7 ist
 * UND eine getestete Variante match_ratio > 0.7 erreicht, kommt im UI ein
 * "Empfohlen: aktivieren" Hint. Der User entscheidet, das System schiebt nicht.
 */
import { useState, useEffect, useCallback } from "react";
import { api, type AnchorMeasure, type PreprocessVariant } from "../../lib/api";

type VariantType = "clahe" | "sauvola" | "hough_redraw";

const VARIANT_LABELS: Record<VariantType, string> = {
  clahe: "CLAHE (Kontrast-Boost)",
  sauvola: "Sauvola (Binarisierung)",
  hough_redraw: "Hough Redraw (Linien rekonstruieren)",
};

const VARIANT_DEFAULTS: Record<VariantType, Record<string, number | string>> = {
  clahe: { clip_limit: 3.0, tile_grid_size: 8 },
  sauvola: { window: 25, k: 0.2 },
  hough_redraw: { line_thickness: 2, min_line_length_frac: 0.4, max_line_gap_frac: 0.02 },
};

export function ImagePreprocessLab({ cardId }: { cardId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          padding: "5px 12px", fontSize: 12, fontWeight: 600,
          background: open ? "#6b7280" : "#1d4ed8",
          color: "white", border: 0, borderRadius: 4, cursor: "pointer",
          alignSelf: "flex-start",
        }}
        title="Öffnet das Image-Preprocessing-Lab für diese Karte: testet CLAHE / Sauvola / Hough auf der Original-PDF, vergleicht Anchor-Quality, ermöglicht eine Variante als OCR-Input zu aktivieren."
      >
        {open ? "🔬 Preprocessing-Lab schließen" : "🔬 Preprocessing-Lab öffnen"}
      </button>
      {open && (
        <div style={{ marginTop: 4, padding: 12, border: "1px solid #d6e3ff", borderRadius: 4, background: "#f5f9ff" }}>
          <LabBody cardId={cardId} />
        </div>
      )}
    </div>
  );
}

function LabBody({ cardId }: { cardId: string }) {
  const [state, setState] = useState<{
    active_variant: string;
    variants: PreprocessVariant[];
  } | null>(null);
  const [baseline, setBaseline] = useState<AnchorMeasure | null>(null);
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testingType, setTestingType] = useState<VariantType | null>(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.preprocess.list(cardId);
      setState({ active_variant: r.active_variant, variants: r.variants });
      setError(null);
    } catch (e: any) {
      setError(e.message);
    }
  }, [cardId]);

  useEffect(() => { reload(); }, [reload]);

  const measureBaseline = useCallback(async () => {
    setBaselineLoading(true);
    try {
      const r = await api.preprocess.baseline(cardId);
      setBaseline(r.baseline);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBaselineLoading(false);
    }
  }, [cardId]);

  const testVariant = useCallback(async (type: VariantType) => {
    setTestingType(type);
    try {
      const params = VARIANT_DEFAULTS[type];
      await api.preprocess.test(cardId, type, params);
      await reload();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setTestingType(null);
    }
  }, [cardId, reload]);

  const activate = useCallback(async (variantId: string) => {
    try {
      await api.preprocess.activate(cardId, variantId);
      await reload();
    } catch (e: any) {
      setError(e.message);
    }
  }, [cardId, reload]);

  if (!state) return <div style={{ fontSize: 12, color: "#6b7280" }}>lädt…</div>;
  if (error) return <div style={{ fontSize: 12, color: "#b91c1c" }}>Fehler: {error}</div>;

  const bestMatchRatio = state.variants.reduce((m, v) => Math.max(m, v.measure.match_ratio), 0);
  const baselineMatchRatio = baseline?.match_ratio ?? null;
  const recommendation =
    baselineMatchRatio !== null && baselineMatchRatio < 0.7 && bestMatchRatio >= 0.7
      ? "Empfehlung: aktiviere die beste Variante (match_ratio ≥ 0.7)"
      : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Vorschau-Pane */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <PreviewPane title="Original (Source of Truth)" url={api.preprocess.imageUrl(cardId, "original")} kind="pdf" />
        <PreviewPane
          title={`Aktive Variante: ${state.active_variant}`}
          url={api.preprocess.imageUrl(cardId, "active")}
          kind={state.active_variant === "original" ? "pdf" : "png"}
        />
      </div>

      {/* Baseline-Measure */}
      <div style={{ padding: 8, background: "#fff", border: "1px solid #d1d5db", borderRadius: 4, fontSize: 12 }}>
        <strong>Baseline (Original):</strong>{" "}
        {baseline ? (
          <span>
            anchors {baseline.anchors_found}, match_ratio {baseline.match_ratio.toFixed(2)},
            form_type {baseline.form_type}
          </span>
        ) : (
          <button onClick={measureBaseline} disabled={baselineLoading}
                  style={{ marginLeft: 6, padding: "2px 8px", fontSize: 11, cursor: "pointer" }}>
            {baselineLoading ? "misst…" : "messen"}
          </button>
        )}
      </div>

      {recommendation && (
        <div style={{
          padding: 8, background: "#fef3c7", border: "1px solid #f59e0b",
          borderRadius: 4, fontSize: 12, color: "#92400e",
        }}>
          ⚠ {recommendation}
        </div>
      )}

      {/* Test-Buttons für die 3 Varianten */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {(Object.keys(VARIANT_LABELS) as VariantType[]).map(type => (
          <button
            key={type}
            onClick={() => testVariant(type)}
            disabled={testingType !== null}
            style={{
              padding: "6px 12px", fontSize: 12, fontWeight: 600,
              background: testingType === type ? "#9ca3af" : "#3b82f6",
              color: "white", border: 0, borderRadius: 4, cursor: "pointer",
            }}
          >
            {testingType === type ? `Teste ${VARIANT_LABELS[type]}…` : `+ ${VARIANT_LABELS[type]} testen`}
          </button>
        ))}
      </div>

      {/* Varianten-Tabelle */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#e5e7eb", textAlign: "left" }}>
              <th style={{ padding: "6px 10px" }}>Variante</th>
              <th style={{ padding: "6px 10px", textAlign: "right" }}>Anchors</th>
              <th style={{ padding: "6px 10px", textAlign: "right" }}>Match Ratio</th>
              <th style={{ padding: "6px 10px" }}>Form-Type</th>
              <th style={{ padding: "6px 10px" }}>Aktion</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderTop: "1px solid #d1d5db", background: state.active_variant === "original" ? "#dcfce7" : "transparent" }}>
              <td style={{ padding: "6px 10px", fontFamily: "monospace" }}>original</td>
              <td style={{ padding: "6px 10px", textAlign: "right" }}>{baseline?.anchors_found ?? "—"}</td>
              <td style={{ padding: "6px 10px", textAlign: "right" }}>{baseline?.match_ratio.toFixed(2) ?? "—"}</td>
              <td style={{ padding: "6px 10px" }}>{baseline?.form_type ?? "—"}</td>
              <td style={{ padding: "6px 10px" }}>
                {state.active_variant === "original"
                  ? <span style={{ color: "#16a34a", fontWeight: 600 }}>✓ aktiv</span>
                  : <button onClick={() => activate("original")}
                            style={{ padding: "2px 8px", fontSize: 11, cursor: "pointer" }}>
                      ← zum Original
                    </button>
                }
              </td>
            </tr>
            {state.variants.map(v => {
              const isActive = state.active_variant === v.id;
              const isBest = v.measure.match_ratio === bestMatchRatio && bestMatchRatio > 0;
              return (
                <tr key={v.id} style={{
                  borderTop: "1px solid #d1d5db",
                  background: isActive ? "#dcfce7" : isBest ? "#fefce8" : "transparent",
                }}>
                  <td style={{ padding: "6px 10px", fontFamily: "monospace" }}>{v.id}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right" }}>{v.measure.anchors_found}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right" }}>{v.measure.match_ratio.toFixed(2)}</td>
                  <td style={{ padding: "6px 10px" }}>{v.measure.form_type}</td>
                  <td style={{ padding: "6px 10px" }}>
                    {isActive
                      ? <span style={{ color: "#16a34a", fontWeight: 600 }}>✓ aktiv</span>
                      : <button onClick={() => activate(v.id)}
                                style={{ padding: "2px 8px", fontSize: 11, cursor: "pointer" }}>
                          aktivieren
                        </button>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11, color: "#6b7280", fontStyle: "italic" }}>
        Hinweis: nach Aktivierung einer Variante muss die OCR neu gestartet werden, damit der Worker
        die PNG der Variante statt des Original-PDFs als Input nutzt.
      </div>
    </div>
  );
}

function PreviewPane({ title, url, kind }: { title: string; url: string; kind: "pdf" | "png" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>{title}</div>
      <div style={{ border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", overflow: "hidden", height: 380 }}>
        {kind === "pdf" ? (
          <iframe src={url} style={{ width: "100%", height: "100%", border: 0 }} title={title} />
        ) : (
          <img src={url} alt={title} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        )}
      </div>
    </div>
  );
}
